import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import {
  ClipboardCheck,
  Plus,
  Edit2,
  Trash2,
  ListChecks,
  CheckSquare,
  ChevronRight,
  Palette,
  Clock,
  GripVertical,
  Camera,
  AlertCircle,
  Save,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const colorOptions = [
  { value: '#0ea5e9', label: 'Sky Blue' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#84cc16', label: 'Lime' },
];

const responseTypes = [
  { value: 'pass_fail', label: 'Pass/Fail' },
  { value: 'numeric', label: 'Numeric Value' },
  { value: 'text', label: 'Text Input' },
  { value: 'photo', label: 'Photo Only' },
];

export default function VisitTemplatesPage() {
  const [visitTypes, setVisitTypes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Dialog states
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showCheckDialog, setShowCheckDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingCheck, setEditingCheck] = useState(null);
  
  // Form states
  const [typeForm, setTypeForm] = useState({
    name: '',
    description: '',
    color: '#0ea5e9',
    estimated_duration: '4 hours',
  });
  
  const [taskForm, setTaskForm] = useState({
    name: '',
    description: '',
    sequence_order: 1,
    estimated_minutes: 30,
  });
  
  const [checkForm, setCheckForm] = useState({
    name: '',
    description: '',
    response_type: 'pass_fail',
    photo_required: false,
    is_mandatory: true,
    sequence_order: 1,
    numeric_min: '',
    numeric_max: '',
    numeric_unit: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, tasksRes, checksRes] = await Promise.all([
        axios.get(`${API}/visit-types`),
        axios.get(`${API}/visit-tasks`),
        axios.get(`${API}/visit-checks`),
      ]);
      setVisitTypes(typesRes.data);
      setTasks(tasksRes.data);
      setChecks(checksRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Visit Type CRUD
  const handleSaveType = async () => {
    try {
      if (editingType) {
        await axios.patch(`${API}/visit-types/${editingType.id}`, typeForm);
      } else {
        await axios.post(`${API}/visit-types`, typeForm);
      }
      setShowTypeDialog(false);
      setEditingType(null);
      setTypeForm({ name: '', description: '', color: '#0ea5e9', estimated_duration: '4 hours' });
      fetchData();
    } catch (error) {
      console.error('Failed to save visit type:', error);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this visit type? All associated tasks and checks will also be removed.')) return;
    try {
      await axios.delete(`${API}/visit-types/${typeId}`);
      setSelectedType(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete visit type:', error);
    }
  };

  const openEditType = (type) => {
    setEditingType(type);
    setTypeForm({
      name: type.name,
      description: type.description,
      color: type.color,
      estimated_duration: type.estimated_duration,
    });
    setShowTypeDialog(true);
  };

  // Task CRUD
  const handleSaveTask = async () => {
    try {
      const taskData = {
        ...taskForm,
        visit_type_id: selectedType.id,
        visit_type_name: selectedType.name,
      };
      
      if (editingTask) {
        await axios.patch(`${API}/visit-tasks/${editingTask.id}`, taskData);
      } else {
        await axios.post(`${API}/visit-tasks`, taskData);
      }
      setShowTaskDialog(false);
      setEditingTask(null);
      setTaskForm({ name: '', description: '', sequence_order: tasks.filter(t => t.visit_type_id === selectedType?.id).length + 1, estimated_minutes: 30 });
      fetchData();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task? All associated checks will also be removed.')) return;
    try {
      await axios.delete(`${API}/visit-tasks/${taskId}`);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      name: task.name,
      description: task.description,
      sequence_order: task.sequence_order,
      estimated_minutes: task.estimated_minutes,
    });
    setShowTaskDialog(true);
  };

  // Check CRUD
  const handleSaveCheck = async () => {
    try {
      const checkData = {
        ...checkForm,
        task_id: selectedTask.id,
        task_name: selectedTask.name,
        visit_type_id: selectedType.id,
        visit_type_name: selectedType.name,
        numeric_min: checkForm.numeric_min ? parseFloat(checkForm.numeric_min) : null,
        numeric_max: checkForm.numeric_max ? parseFloat(checkForm.numeric_max) : null,
      };
      
      if (editingCheck) {
        await axios.patch(`${API}/visit-checks/${editingCheck.id}`, checkData);
      } else {
        await axios.post(`${API}/visit-checks`, checkData);
      }
      setShowCheckDialog(false);
      setEditingCheck(null);
      setCheckForm({
        name: '',
        description: '',
        response_type: 'pass_fail',
        photo_required: false,
        is_mandatory: true,
        sequence_order: checks.filter(c => c.task_id === selectedTask?.id).length + 1,
        numeric_min: '',
        numeric_max: '',
        numeric_unit: '',
      });
      fetchData();
    } catch (error) {
      console.error('Failed to save check:', error);
    }
  };

  const handleDeleteCheck = async (checkId) => {
    if (!window.confirm('Are you sure you want to delete this check?')) return;
    try {
      await axios.delete(`${API}/visit-checks/${checkId}`);
      fetchData();
    } catch (error) {
      console.error('Failed to delete check:', error);
    }
  };

  const openEditCheck = (check) => {
    setEditingCheck(check);
    setCheckForm({
      name: check.name,
      description: check.description || '',
      response_type: check.response_type,
      photo_required: check.photo_required,
      is_mandatory: check.is_mandatory,
      sequence_order: check.sequence_order,
      numeric_min: check.numeric_min || '',
      numeric_max: check.numeric_max || '',
      numeric_unit: check.numeric_unit || '',
    });
    setShowCheckDialog(true);
  };

  const getTasksForType = (typeId) => tasks.filter(t => t.visit_type_id === typeId);
  const getChecksForTask = (taskId) => checks.filter(c => c.task_id === taskId);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Visit Templates</h1>
            <p className="text-slate-500">Configure visit types, tasks, and checks for site visits</p>
          </div>
          <Button
            onClick={() => {
              setEditingType(null);
              setTypeForm({ name: '', description: '', color: '#0ea5e9', estimated_duration: '4 hours' });
              setShowTypeDialog(true);
            }}
            data-testid="create-visit-type"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Visit Type
          </Button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Visit Types List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-sky-500" />
                Visit Types
              </CardTitle>
              <CardDescription>Select a type to manage its tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {visitTypes.map((type) => (
                  <div
                    key={type.id}
                    onClick={() => {
                      setSelectedType(type);
                      setSelectedTask(null);
                    }}
                    className={cn(
                      'p-3 rounded-lg border cursor-pointer transition-all',
                      selectedType?.id === type.id
                        ? 'border-sky-500 bg-sky-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    )}
                    data-testid={`visit-type-${type.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        <div>
                          <p className="font-medium text-slate-900">{type.name}</p>
                          <p className="text-xs text-slate-500">{getTasksForType(type.id).length} tasks</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditType(type);
                          }}
                          data-testid={`edit-type-${type.id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteType(type.id);
                          }}
                          data-testid={`delete-type-${type.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
                {visitTypes.length === 0 && (
                  <p className="text-center text-slate-500 py-4">No visit types configured</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Tasks & Checks Panel */}
          <Card className="lg:col-span-2">
            {selectedType ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: selectedType.color }}
                      />
                      <div>
                        <CardTitle className="text-lg">{selectedType.name}</CardTitle>
                        <CardDescription>{selectedType.description || 'No description'}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {selectedType.estimated_duration}
                      </Badge>
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditingTask(null);
                          setTaskForm({
                            name: '',
                            description: '',
                            sequence_order: getTasksForType(selectedType.id).length + 1,
                            estimated_minutes: 30,
                          });
                          setShowTaskDialog(true);
                        }}
                        data-testid="add-task"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Task
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Accordion type="single" collapsible className="w-full">
                    {getTasksForType(selectedType.id)
                      .sort((a, b) => a.sequence_order - b.sequence_order)
                      .map((task, index) => (
                        <AccordionItem key={task.id} value={task.id}>
                          <AccordionTrigger className="px-4 hover:no-underline hover:bg-slate-50">
                            <div className="flex items-center gap-3 flex-1">
                              <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center font-medium">
                                {index + 1}
                              </span>
                              <div className="text-left flex-1">
                                <p className="font-medium text-slate-900">{task.name}</p>
                                <p className="text-xs text-slate-500">
                                  {getChecksForTask(task.id).length} checks • {task.estimated_minutes} min
                                </p>
                              </div>
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-slate-100 cursor-pointer"
                                  onClick={() => openEditTask(task)}
                                  data-testid={`edit-task-${task.id}`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </div>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-red-100 cursor-pointer text-red-500 hover:text-red-600"
                                  onClick={() => handleDeleteTask(task.id)}
                                  data-testid={`delete-task-${task.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </div>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="ml-9 space-y-2">
                              {task.description && (
                                <p className="text-sm text-slate-600 mb-3">{task.description}</p>
                              )}
                              
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-slate-700">Checks</p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTask(task);
                                    setEditingCheck(null);
                                    setCheckForm({
                                      name: '',
                                      description: '',
                                      response_type: 'pass_fail',
                                      photo_required: false,
                                      is_mandatory: true,
                                      sequence_order: getChecksForTask(task.id).length + 1,
                                      numeric_min: '',
                                      numeric_max: '',
                                      numeric_unit: '',
                                    });
                                    setShowCheckDialog(true);
                                  }}
                                  data-testid={`add-check-${task.id}`}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Check
                                </Button>
                              </div>
                              
                              <div className="space-y-2">
                                {getChecksForTask(task.id)
                                  .sort((a, b) => a.sequence_order - b.sequence_order)
                                  .map((check) => (
                                    <div
                                      key={check.id}
                                      className="flex items-center justify-between p-2 bg-slate-50 rounded-md border border-slate-100"
                                    >
                                      <div className="flex items-center gap-2">
                                        <CheckSquare className="w-4 h-4 text-slate-400" />
                                        <div>
                                          <p className="text-sm font-medium text-slate-800">{check.name}</p>
                                          <div className="flex items-center gap-2 text-xs text-slate-500">
                                            <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                              {responseTypes.find(r => r.value === check.response_type)?.label}
                                            </Badge>
                                            {check.photo_required && (
                                              <span className="flex items-center gap-0.5">
                                                <Camera className="w-3 h-3" /> Photo
                                              </span>
                                            )}
                                            {check.is_mandatory && (
                                              <span className="flex items-center gap-0.5 text-amber-600">
                                                <AlertCircle className="w-3 h-3" /> Required
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={() => {
                                            setSelectedTask(task);
                                            openEditCheck(check);
                                          }}
                                          data-testid={`edit-check-${check.id}`}
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-red-500"
                                          onClick={() => handleDeleteCheck(check.id)}
                                          data-testid={`delete-check-${check.id}`}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                {getChecksForTask(task.id).length === 0 && (
                                  <p className="text-sm text-slate-400 italic">No checks configured</p>
                                )}
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                  </Accordion>
                  {getTasksForType(selectedType.id).length === 0 && (
                    <div className="p-8 text-center text-slate-500">
                      <ListChecks className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No tasks configured for this visit type</p>
                      <p className="text-sm">Click "Add Task" to get started</p>
                    </div>
                  )}
                </CardContent>
              </>
            ) : (
              <div className="h-full flex items-center justify-center py-16">
                <div className="text-center text-slate-500">
                  <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-medium">Select a Visit Type</p>
                  <p className="text-sm">Choose a visit type from the left to manage its tasks and checks</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Visit Type Dialog */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Visit Type' : 'Create Visit Type'}</DialogTitle>
            <DialogDescription>
              Configure a template for site visits. Tasks and checks will be automatically loaded when creating visits of this type.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="type-name">Name</Label>
              <Input
                id="type-name"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="e.g., Routine Inspection"
                data-testid="type-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type-description">Description</Label>
              <Textarea
                id="type-description"
                value={typeForm.description}
                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                placeholder="Describe the purpose and scope of this visit type..."
                data-testid="type-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Color</Label>
                <Select value={typeForm.color} onValueChange={(v) => setTypeForm({ ...typeForm, color: v })}>
                  <SelectTrigger data-testid="type-color-select">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: typeForm.color }} />
                      <span>{colorOptions.find(c => c.value === typeForm.color)?.label}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color.value }} />
                          <span>{color.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="type-duration">Estimated Duration</Label>
                <Select value={typeForm.estimated_duration} onValueChange={(v) => setTypeForm({ ...typeForm, estimated_duration: v })}>
                  <SelectTrigger data-testid="type-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1 hour">1 hour</SelectItem>
                    <SelectItem value="2 hours">2 hours</SelectItem>
                    <SelectItem value="4 hours">4 hours</SelectItem>
                    <SelectItem value="6 hours">6 hours</SelectItem>
                    <SelectItem value="8 hours">8 hours</SelectItem>
                    <SelectItem value="Full day">Full day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTypeDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveType} data-testid="save-type-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
            <DialogDescription>
              Configure a task for {selectedType?.name}. Tasks group related checks together.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="task-name">Task Name</Label>
              <Input
                id="task-name"
                value={taskForm.name}
                onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                placeholder="e.g., Visual Inspection"
                data-testid="task-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Describe what this task involves..."
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="task-order">Order</Label>
                <Input
                  id="task-order"
                  type="number"
                  min="1"
                  value={taskForm.sequence_order}
                  onChange={(e) => setTaskForm({ ...taskForm, sequence_order: parseInt(e.target.value) || 1 })}
                  data-testid="task-order-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="task-minutes">Est. Minutes</Label>
                <Input
                  id="task-minutes"
                  type="number"
                  min="1"
                  value={taskForm.estimated_minutes}
                  onChange={(e) => setTaskForm({ ...taskForm, estimated_minutes: parseInt(e.target.value) || 30 })}
                  data-testid="task-minutes-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTask} data-testid="save-task-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingTask ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check Dialog */}
      <Dialog open={showCheckDialog} onOpenChange={setShowCheckDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingCheck ? 'Edit Check' : 'Add Check'}</DialogTitle>
            <DialogDescription>
              Configure a check item for {selectedTask?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="check-name">Check Name</Label>
              <Input
                id="check-name"
                value={checkForm.name}
                onChange={(e) => setCheckForm({ ...checkForm, name: e.target.value })}
                placeholder="e.g., Check panel temperature"
                data-testid="check-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="check-description">Description (Optional)</Label>
              <Textarea
                id="check-description"
                value={checkForm.description}
                onChange={(e) => setCheckForm({ ...checkForm, description: e.target.value })}
                placeholder="Additional instructions for this check..."
                data-testid="check-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Response Type</Label>
                <Select value={checkForm.response_type} onValueChange={(v) => setCheckForm({ ...checkForm, response_type: v })}>
                  <SelectTrigger data-testid="check-response-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {responseTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="check-order">Order</Label>
                <Input
                  id="check-order"
                  type="number"
                  min="1"
                  value={checkForm.sequence_order}
                  onChange={(e) => setCheckForm({ ...checkForm, sequence_order: parseInt(e.target.value) || 1 })}
                  data-testid="check-order-input"
                />
              </div>
            </div>
            
            {checkForm.response_type === 'numeric' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="check-min">Min Value</Label>
                  <Input
                    id="check-min"
                    type="number"
                    value={checkForm.numeric_min}
                    onChange={(e) => setCheckForm({ ...checkForm, numeric_min: e.target.value })}
                    placeholder="0"
                    data-testid="check-min-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="check-max">Max Value</Label>
                  <Input
                    id="check-max"
                    type="number"
                    value={checkForm.numeric_max}
                    onChange={(e) => setCheckForm({ ...checkForm, numeric_max: e.target.value })}
                    placeholder="100"
                    data-testid="check-max-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="check-unit">Unit</Label>
                  <Input
                    id="check-unit"
                    value={checkForm.numeric_unit}
                    onChange={(e) => setCheckForm({ ...checkForm, numeric_unit: e.target.value })}
                    placeholder="°C"
                    data-testid="check-unit-input"
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  id="check-photo"
                  checked={checkForm.photo_required}
                  onCheckedChange={(v) => setCheckForm({ ...checkForm, photo_required: v })}
                  data-testid="check-photo-switch"
                />
                <Label htmlFor="check-photo" className="flex items-center gap-1">
                  <Camera className="w-4 h-4" />
                  Photo Required
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="check-mandatory"
                  checked={checkForm.is_mandatory}
                  onCheckedChange={(v) => setCheckForm({ ...checkForm, is_mandatory: v })}
                  data-testid="check-mandatory-switch"
                />
                <Label htmlFor="check-mandatory" className="flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Mandatory
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCheck} data-testid="save-check-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingCheck ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
